import React, { Fragment, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// -----------------------------------------------------------------------------
//  Flowroom Landing Page (React + Tailwind 3) – **fully‑responsive**
//  ---------------------------------------------------------------------------
//  • Plain React – no Next.js APIs.
//  • Ready for Module Federation (expose this file as `./LandingPage`).
//  • Requires Tailwind + PostCSS set‑up in the host repo.
//  • All decorative blobs implemented with CSS gradients so you don’t ship
//    extra PNGs. Swap a gradient <div> for an <img> if you prefer assets.
// -----------------------------------------------------------------------------

/* ---------------------------------------------------------------------------
   Shared UI helpers
   --------------------------------------------------------------------------- */
interface ButtonProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
  action?: string; // For postMessage actions
}

const Button = ({ to, children, className = "", onClick, style, action }: ButtonProps) => {
  const handleClick = (e: React.MouseEvent) => {
    if (action) {
      e.preventDefault();
      // Send message to parent if in iframe
      if (window.parent !== window) {
        console.log('Page is in iframe - sending authModal message to parent window');
        window.parent.postMessage('authModal', '*');
      } else {
        console.log(`Page is NOT in iframe - redirecting to ${to}`);
        // Normal navigation if not in iframe
        window.location.href = to;
      }
    }
    
    // Call the original onClick handler if provided
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`inline-flex items-center justify-center rounded-md bg-green-500 px-6 py-3 text-sm font-medium shadow transition-colors hover:bg-green-600 ${className}`}
      style={{ color: '#1C1C1C', ...style }}
    >
      {children}
    </Link>
  );
};

/* ---------------------------------------------------------------------------
   Decorative Blob helper (pure‑CSS radial + blur)
   --------------------------------------------------------------------------- */
const Blob = ({ className = "", color = "rgba(236,72,153,0.35)", size = 384 }) => (
  <div
    aria-hidden
    className={`pointer-events-none absolute rounded-full blur-[120px] ${className}`}
    style={{ width: size, height: size, background: `radial-gradient(circle at center, ${color} 0%, transparent 70%)` }}
  />
);

/* ---------------------------------------------------------------------------
   Page component
   --------------------------------------------------------------------------- */
export default function FlowroomLandingPage() {
  /* ---------------------------------------------------------------
     Mobile nav (simple disclosure)
  ----------------------------------------------------------------*/
  const [open, setOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  
  // Log when component mounts to confirm message handlers are initialized
  useEffect(() => {
    console.log('Login/signup handlers initialized');
  }, []);

  return (
    <>
      {/* Nebula background */}
      <div 
        className="absolute inset-0 -z-50 nebula-top-bg"
        style={{ backgroundImage: 'url(/images/nebula-top-bg.png)' }}
      />
      
    <main className="min-h-screen flex flex-col font-sans text-white relative overflow-x-hidden"
          style={{ background: 'transparent' }}>

      {/* Content wrapper - grows to push footer to bottom */}
      <div className="flex-1 flex flex-col">

      {/* ---------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ---------------------------------------------------------------- */}
      <header className="w-full h-16 flex items-center max-w-7xl mx-auto px-6 relative" style={{ background: 'none', backgroundColor: 'transparent', backdropFilter: 'none', marginTop: '10px' }}>
        <Link to="/" className="flex items-center">
          <img src="/images/flowroom-logo.png" alt="Flowroom" style={{ height: '20px' }} />
        </Link>

        {/* Desktop nav */}
        <nav className="ml-auto hidden md:flex gap-6 items-center text-sm">
          <Link 
            to="/signin" 
            className="hover:underline text-base font-quicksand"
            onClick={(e) => {
              e.preventDefault();
              // Send message to parent if in iframe
              if (window.parent !== window) {
                console.log('Page is in iframe - sending authModal message to parent window');
                window.parent.postMessage('authModal', '*');
              } else {
                console.log('Page is NOT in iframe - redirecting to /signin');
                window.location.href = '/signin';
              }
            }}
          >
            Sign in
          </Link>
          <Button 
            to="/signup" 
            className="px-4 py-2 text-sm font-quicksand" 
            style={{ backgroundColor: '#4FD974', borderRadius: '10px', fontWeight: 'bolder' }}
            action="signup"
          >
            Sign up
          </Button>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="ml-auto md:hidden text-lg focus:outline-none"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          ☰
        </button>

        {open && (
          <div className="absolute top-full right-6 mt-4 bg-neutral-900 border border-neutral-800 rounded-lg py-4 px-6 flex flex-col gap-4 md:hidden z-20 shadow-lg">
            <Link 
              to="/signin" 
              className="hover:underline text-base font-quicksand" 
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                if (window.parent !== window) {
                  console.log('Sending authModal message to parent window');
                  window.parent.postMessage('authModal', '*');
                } else {
                  window.location.href = '/signin';
                }
              }}
            >
              Sign in
            </Link>
            <Button 
              to="/signup" 
              onClick={() => setOpen(false)} 
              className="font-quicksand" 
              style={{ backgroundColor: '#4FD974', borderRadius: '10px', fontWeight: 'bolder' }}
              action="signup"
            >
              Sign up
            </Button>
          </div>
        )}
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* Hero                                                             */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden pt-24 pb-12" style={{ background: 'transparent' }}>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Hero accent blob - temporarily removed to test header transparency */}
          {/* <Blob className="-top-52 left-0 w-[500px]" color="rgba(168,85,247,0.35)" size={500} /> */}

          <div className="max-w-2xl">

        <h1
          className="text-3xl sm:text-5xl font-semibold leading-tight font-quicksand"
        >
          Bringing <span className="text-green-400">Powerful, Creative</span>
          <br />
          and useful AI Apps Together – <br />
          <span className="gradient-text">All in One Super App.</span>
        </h1>

            <p className="mt-6 text-base sm:text-lg opacity-80 font-quicksand">Discover, Remix, Share and Create</p>

            <Button 
              to="https://flowroom.com/filters/featured" 
              className="mt-10 max-w-[175px] relative z-20 px-4 py-2 sm:px-6 sm:py-3 text-xs sm:text-sm"
              onClick={(e) => {
                e.preventDefault();
                if (window.parent !== window) {
                  window.parent.location.href = 'https://flowroom.com/filters/featured';
                } else {
                  window.location.href = 'https://flowroom.com/filters/featured';
                }
              }}
            >
              Explore Apps →
            </Button>
          </div>
        </div>

        <div className="relative w-full lg:max-w-7xl lg:mx-auto lg:px-6 -mt-16">
          <div className="relative z-0">
            <img
              src="/images/thumbnails-2.png"
              alt="App thumbnails"
              className="w-full h-auto object-cover lg:object-contain opacity-90"
              loading="eager"
            />
            {/* Fade effect at bottom - stronger fade to black like in reference */}
            <div className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 md:h-48 lg:h-64 bg-gradient-to-t from-black via-black/90 to-transparent rounded-b-2xl"></div>
            {/* Play button overlay - much larger like in reference */}
            <button 
              className="absolute bottom-4 sm:bottom-8 md:bottom-12 lg:bottom-16 left-1/2 transform -translate-x-1/2 group"
              onClick={() => setVideoModalOpen(true)}
              aria-label="Play demo video"
            >
              <img
                src="/images/play-button.png"
                alt="Play button"
                className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </button>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Mini‑apps teaser                                                */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden py-12 backdrop-blur-xl" style={{ backgroundColor: '#0A0C10' }}>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center text-2xl sm:text-3xl font-semibold font-quicksand"
        >
          Find AI Apps that fit your needs
        </motion.h2>
        
        <motion.h3
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-center text-xl sm:text-2xl font-medium mt-4 gradient-text"
        >
          Or Create them (No Code Needed)
        </motion.h3>

        <div className="relative w-full max-w-3xl mx-auto mt-16">
          <img
            src="/images/mini-apps.png"
            alt="Mini apps"
            className="w-full h-auto rounded-xl shadow-xl"
            loading="lazy"
          />
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Discover flows 2‑Col                                            */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden max-w-6xl mx-auto py-12 px-6" style={{ backgroundColor: '#0A0C10' }}>
        
        <div className="grid gap-12 md:grid-cols-2 relative">
          {/* Background gradient positioned behind icons only */}
          <div 
            className="absolute top-1/2 md:top-0 left-0 md:right-0 md:left-auto w-full md:w-1/2 h-1/2 md:h-full opacity-80"
            style={{ 
              backgroundImage: 'url(/images/linear-gradient-discover-section.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        <div className="relative z-20">
          <h3 className="text-2xl sm:text-3xl font-semibold mb-6 font-quicksand text-white">
            Discover AI powered apps
            <br />
            <span className="gradient-text">What we call flows</span>
          </h3>
          <p className="leading-relaxed opacity-80 mb-8">
            For personal use, fun, business, and audience engagement. Instead of paying for multiple AI apps separately, Flowroom gives you access to a growing library of free and premium AI apps, all in one platform. With one subscription, you get credits you can apply across different AI apps, so you're never locked into just one.
          </p>
          <p className="leading-relaxed opacity-80">
            For content creators & influencers, you can use or create AI-powered mini apps to complement your content and engage your audience with interactive AI experiences, innovative ways to expand your brand. You can enhance your content, provide more value, and create AI apps—all with the simplicity of one platform, no multiple subscriptions needed.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 relative z-10">
          {[
            { title: "Discover AI powered mini apps for creativity, productivity, business and entertainment", icon: "/images/discover-ai-icon.png" },
            { title: "Remix existing mini apps to fit your needs- no technical skills needed", icon: "/images/remix-existing-icon.png" },
            { title: "Create AI apps instantly using your words - no code needed", icon: "/images/create-icon.png" },
            { title: "Save time and money - no need to download multiple apps or juggle multiple subscriptions", icon: "/images/clock-icon.png" },
            { title: "Find free and premium AI apps - all in one platform", icon: "/images/free-icon.png" },
            { title: "For content creators: Enhance your content, promote your brand, or experiment with AI powered experiences", icon: "/images/content-creator-icon.png" },
            { title: "Engage with AI in ways that go beyond chat-based interfaces", icon: "/images/ai-interactive.png" },
            { title: "A hub where AI innovators, creators and developers collaborate", icon: "/images/ai-hub-icon.png" },
            { title: "No installation, no downloads, - just instant AI - powered mini apps", icon: "/images/no-installation-icon.png" }
          ].map(
            (item, index) => (
              <div key={index} className="flex flex-col items-start gap-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <img src={item.icon} alt="" className="w-full h-full object-contain" />
                </div>
                <p className="text-xs opacity-80 leading-relaxed text-left">{item.title}</p>
              </div>
            )
          )}
        </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Interactive phone cards                                         */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden py-12 pb-24" style={{ backgroundColor: '#0A0C10' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-12 md:gap-12 items-start justify-center">
            {/* Text content - first on mobile, right on desktop */}
            <div className="order-1 md:order-2 font-quicksand text-left max-w-2xl mx-auto md:mx-0">
              <h3 className="text-2xl sm:text-3xl font-semibold mb-6">
                <span className="text-white">Brand Apps</span>
              </h3>
              <h4 className="text-xl sm:text-2xl font-medium mb-6 gradient-text">
                Monetize Your Expertise and Stand Out with Tailored AI Experiences
              </h4>
              <p className="leading-relaxed opacity-80 mb-6">
                In a world where AI can build or copy almost any app from text prompts, apps containing your unique, exclusive content and personal expertise remain your strongest competitive advantage. Flowroom offers no-code tools (plus an optional code editor) so you can create or remix interactive experiences—built around your videos, data, or insights—and develop bots custom-trained on your knowledge and personality.
              </p>
              <p className="leading-relaxed opacity-80 mb-4">
                Like Chef Rush's "Grub GPT," a chatbot that delivers exclusive recipe videos, step-by-step instructions, and personal cooking insights, or his "Self Reflect" AI-powered journal, which guides users through mindfulness and reflection (without providing professional mental health services). Another example is Chelsko's interactive YouTube app, serving up relevant clips in response to viewers' questions. These solutions amplify the value content creators offer—deepening audience engagement, and create new revenue opportunities. Discover how others are innovating—and learn how you can do the same.
              </p>
              <p className="leading-relaxed opacity-80">
                <Link 
                  to="https://flowroom.com/filters/featured" 
                  className="underline" 
                  style={{ color: '#4FD975', textDecorationColor: '#4FD975' }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.parent !== window) {
                      window.parent.location.href = 'https://flowroom.com/filters/featured';
                    } else {
                      window.location.href = 'https://flowroom.com/filters/featured';
                    }
                  }}
                >
                  Learn how influencers are using this to amplify their content
                </Link>
              </p>
            </div>
            
            {/* App group - second on mobile, left on desktop */}
            <div className="order-2 md:order-1 flex flex-col justify-center md:-mt-16 lg:-mt-24">
              <div className="relative overflow-hidden rounded-xl shadow-2xl">
                <img src="/images/group-of-apps.png" alt="Group of apps" className="w-full h-auto object-contain" loading="lazy" />
              </div>
              
              {/* Text descriptions - in normal flow below the image */}
              <div className="flex flex-col md:flex-row justify-between px-2 md:px-4 lg:px-8 gap-4 text-xs lg:text-sm mt-6">
                <div className="flex-1 text-left">
                  <div className="font-medium mb-1" style={{ color: '#CA48DC' }}>
                    Your personal culinary assistant powered by Flowroom!
                  </div>
                  <div className="text-white text-xs">
                    Trained by a world-renowned celebrity chef who's cooked for presidents, GrubGPT transforms your kitchen into a culinary studio.
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium mb-1" style={{ color: '#CA48DC' }}>
                    Your personal confidence & relationship coach, right at your fingertips!
                  </div>
                  <div className="text-white text-xs">
                    Coach Chelsea helps you build confidence and connect better, through her YouTube channel and in-app chat.
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium mb-1" style={{ color: '#CA48DC' }}>
                    Your guided space for self-discovering, clarity, and emotional growth!
                  </div>
                  <div className="text-white text-xs">
                    Chat with an AI clone of yourself to reflect, set goals, and navigate tough conversations, growing through insight and self-dialogue.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Mobile Desktop Banner                                           */}
      {/* ---------------------------------------------------------------- */}
      <div className="relative w-full flex justify-center py-6" style={{ backgroundColor: '#0A0C10' }}>
        <div className="w-full max-w-lg sm:max-w-xl md:max-w-lg lg:w-full lg:max-w-[55rem] px-4">
          <img 
            src="/images/mobile-desktop-banner.png" 
            alt="Mobile Desktop Banner" 
            className="w-full h-auto object-contain"
            loading="lazy"
          />
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Coder / IDE Promo                                               */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden py-12" style={{ backgroundColor: '#0A0C10' }}>

        <div className="max-w-4xl mx-auto text-center px-6 font-quicksand">
          <h3 className="text-2xl sm:text-3xl font-semibold">
            <span className="text-white">And If</span> <span className="gradient-text">You're A Coder</span>
          </h3>
          <p className="max-w-2xl mx-auto mt-4 opacity-80">
            Build flows in minutes with an in‑browser IDE. Import a sprite sheet, tweak props, hit save — your new AI app
            is live.
          </p>
        </div>

        <div className="relative w-full max-w-sm sm:max-w-lg md:max-w-6xl lg:max-w-7xl mx-auto mt-8 px-2 md:px-6">
          <img src="/images/editor-4.png" alt="IDE screenshot" className="w-full h-auto object-contain rounded-2xl shadow-2xl" />
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Three Feature Bullets + CTA                                     */}
      {/* ---------------------------------------------------------------- */}
      <section className="w-full py-12 relative overflow-hidden" style={{ backgroundColor: '#0A0C10' }}>
        {/* Background gradient */}
        <div 
          className="absolute inset-0 opacity-80"
          style={{ 
            backgroundImage: 'url(/images/appideas-personal-mini-linear-gradient.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-20 md:gap-40 items-center">
          {/* Text content - left on desktop, top on mobile */}
          <div className="order-1 md:order-1 max-w-xl font-quicksand">
            <h3 className="text-2xl sm:text-3xl font-semibold mb-6">The Ultimate All‑in‑One Platform</h3>
            <p className="text-base opacity-80 mb-20 leading-relaxed">
              Just as flows can work in various scenarios, Flowroom as a platform can be useful for various scenarios.
            </p>
            <Button 
              to="https://flowroom.com/filters/recent" 
              className="px-4 py-2 sm:px-8 sm:py-3 bg-green-500 hover:bg-green-600 text-xs sm:text-sm"
              onClick={(e) => {
                e.preventDefault();
                if (window.parent !== window) {
                  window.parent.location.href = 'https://flowroom.com/filters/recent';
                } else {
                  window.location.href = 'https://flowroom.com/filters/recent';
                }
              }}
            >
              Try flowroom →
            </Button>
          </div>

          {/* Feature cards - right on desktop, bottom on mobile */}
          <div className="order-2 md:order-2 flex flex-col gap-6 relative">
            <img src="/images/app-ideas.png" alt="App Ideas" className="w-full max-w-md mx-auto h-auto object-contain rounded-3xl shadow-lg" />
            <img src="/images/personal-needs.png" alt="Personal Needs" className="w-full max-w-lg mx-auto h-auto object-contain rounded-3xl shadow-lg md:-ml-8" />
            <img src="/images/mini-app-profile.png" alt="Mini App Profile" className="w-full max-w-md mx-auto h-auto object-contain rounded-3xl shadow-lg" />
          </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Use‑case gallery                                                */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden py-12 pb-24" style={{ backgroundColor: '#0A0C10' }}>
        {/* Background gradient */}
        <div 
          className="absolute inset-0 -top-48 opacity-80"
          style={{ 
            backgroundImage: 'url(/images/other-flow-usecases-gradient-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        <h3 className="text-center text-2xl sm:text-3xl font-semibold mb-16 font-quicksand relative z-10">
          <span className="text-white">Other flow</span> <span className="gradient-text">Use Cases</span>
        </h3>

        {/* Mobile/Tablet: Stacked layout with text overlays */}
        <div className="flex flex-col gap-8 lg:hidden justify-center items-center relative z-10 px-4">
          {/* Dopple Date app */}
          <div className="relative w-full max-w-xs">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="/images/dopple-date-app.png" alt="Dopple Date app" className="w-full h-auto" loading="lazy" />
            </div>
            {/* Text overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/70 to-transparent rounded-b-lg">
              <div className="text-left text-xs">
                <div className="font-medium mb-1" style={{ color: '#4FD975' }}>
                  Let your clone do the talking.
                </div>
                <div className="text-white">
                  It's dating, but smarter. Automated conversations, real connections. Let your second self find your better half.
                </div>
              </div>
            </div>
          </div>
          
          {/* Fear Aid & Contract Crafter apps */}
          <div className="relative w-full max-w-md">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="/images/fear-aid-contract-crafter.png" alt="Fear Aid & Contract Crafter apps" className="w-full h-auto" loading="lazy" />
            </div>
            {/* Text overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/70 to-transparent rounded-b-lg">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 text-xs">
                <div className="flex-1 text-left">
                  <div className="font-medium mb-1" style={{ color: '#4FD975' }}>
                    Take control of your anxiety with simple, science-backed tools.
                  </div>
                  <div className="text-white">
                    Set goals, use calming techniques, and check in daily to stay grounded and build confidence.
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium mb-1" style={{ color: '#4FD975' }}>
                    Create, sign, and send contracts, all with just your voice.
                  </div>
                  <div className="text-white">
                    Speak your terms. Instantly create, sign, and send contracts, no typing needed.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop: Grid layout with text overlaying bottom of images */}
        <div className="hidden lg:block relative z-10 px-4 max-w-4xl mx-auto">
          {/* Images row - aligned at bottom with text overlays */}
          <div className="flex flex-row gap-4 justify-center items-end">
            {/* DoppleDate */}
            <div className="relative w-60 flex-shrink-0">
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img src="/images/dopple-date-app.png" alt="Dopple Date app" className="w-full h-auto" loading="lazy" />
              </div>
              {/* Text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/70 to-transparent rounded-b-lg">
                <div className="text-left text-xs">
                  <div className="font-medium mb-1" style={{ color: '#4FD975' }}>
                    Let your clone do the talking.
                  </div>
                  <div className="text-white">
                    It's dating, but smarter. Automated conversations, real connections. Let your second self find your better half.
                  </div>
                </div>
              </div>
            </div>
            
            {/* Fear Aid & Contract Crafter */}
            <div className="relative flex-1 max-w-lg">
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img src="/images/fear-aid-contract-crafter.png" alt="Fear Aid & Contract Crafter apps" className="w-full h-auto" loading="lazy" />
              </div>
              {/* Text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/70 to-transparent rounded-b-lg">
                <div className="flex flex-row gap-4 text-xs">
                  <div className="flex-1 text-left">
                    <div className="font-medium mb-1" style={{ color: '#4FD975' }}>
                      Take control of your anxiety with simple, science-backed tools.
                    </div>
                    <div className="text-white">
                      Set goals, use calming techniques, and check in daily to stay grounded and build confidence.
                    </div>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium mb-1" style={{ color: '#4FD975' }}>
                      Create, sign, and send contracts, all with just your voice.
                    </div>
                    <div className="text-white">
                      Speak your terms. Instantly create, sign, and send contracts, no typing needed.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      </div>
      {/* End content wrapper */}

      {/* ---------------------------------------------------------------- */}
      {/* Footer                                                          */}
      {/* ---------------------------------------------------------------- */}
      <footer className="border-t border-neutral-800 mt-auto flex-shrink-0" style={{ backgroundColor: '#0A0C10' }}>
        <div className="max-w-6xl mx-auto py-12 px-6 pb-20 flex flex-col md:flex-row gap-8 md:gap-16 text-sm">
          <div className="flex-1">
            <p className="font-bold mb-4">flowroom</p>
            <p className="opacity-70">© {new Date().getFullYear()} Flowroom Inc.</p>
          </div>

          <nav className="grid grid-cols-2 gap-6 flex-[2] md:flex-none">
            <a href="https://flowroom.com/privacy" className="hover:underline">
              Privacy
            </a>
            <a href="https://flowroom.com/terms" className="hover:underline">
              Terms of Use
            </a>
          </nav>
        </div>
      </footer>

      {/* Video Modal */}
      {videoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl mx-4 aspect-video">
            <button
              onClick={() => setVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-xl"
              aria-label="Close video"
            >
              ✕
            </button>
            <iframe
              className="w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/8Xby94vfufk?autoplay=1"
              title="Flowroom Demo Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </main>
    </>
  );
}
